import { t } from '@lingui/macro'
import { CreateMultiLegShippingEstimateByFuelResponse } from '@lune-climate/lune'
import { Select, Text } from '@lune-fe/lune-ui-lib'
import { Stack } from '@mui/material'
import { FC, useState } from 'react'

import { Divider, LegsByFuelPdfTable, RequestAndResponse } from '../components'
import {
    CalculationResults,
    MultiLegShippingByFuelEmissionExplanation,
    MultiLegShippingByFuelExplanationLegs,
    MultiLegShippingByFuelInputSection,
} from '../components/emissionsCalculationExplanation'
import { getFormattedAmount } from '../utils'

interface MultiLegShippingByFuelEstimateResultProps {
    estimate: CreateMultiLegShippingEstimateByFuelResponse & { _meta: { response: unknown } }
    enableEmissionFactorLink: boolean
    isMobile: boolean
    requestAndResponseSnippets?: {
        isTestMode?: boolean
        testApiKey?: string
    }
    pdfStructure?: boolean
}

type Leg = CreateMultiLegShippingEstimateByFuelResponse['legs'][number]

export const MultiLegShippingByFuelEstimateResult: FC<
    MultiLegShippingByFuelEstimateResultProps
> = ({
    estimate,
    isMobile,
    enableEmissionFactorLink,
    pdfStructure,
    requestAndResponseSnippets,
}) => {
    const { legs } = estimate
    if (
        !pdfStructure &&
        (typeof estimate._meta.response !== 'object' ||
            estimate._meta.response === null ||
            !('request' in estimate._meta.response))
    ) {
        throw new Error('Unexpected _meta object')
    }

    const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null)

    return (
        <>
            {isMobile && (
                <Select<number | ''>
                    value={activeTabIndex ?? ''}
                    items={legs.map((_leg: Leg, index: number) => ({
                        value: index + 1,
                        label: t`Leg ${index + 1}`,
                    }))}
                    label={t`Select leg`}
                    onChange={(value: number | '') => {
                        if (value !== '') {
                            setActiveTabIndex(value)
                        }
                    }}
                />
            )}
            <CalculationResults
                title={'Multi-leg logistics estimate by fuel'}
                amount={estimate.mass.amount}
            />
            <Stack direction={'column'} spacing={9} mt={9}>
                {!pdfStructure && !isMobile && (
                    <>
                        <MultiLegShippingByFuelExplanationLegs
                            estimate={estimate}
                            activeTabIndex={activeTabIndex}
                            setActiveTab={setActiveTabIndex}
                        />
                        <Divider />
                    </>
                )}
                {!pdfStructure ? (
                    activeTabIndex !== null ? (
                        <MultiLegShippingByFuelEmissionExplanation
                            estimate={estimate}
                            enableEmissionFactorLink={enableEmissionFactorLink}
                            leg={activeTabIndex - 1}
                            pdfStructure={pdfStructure}
                        />
                    ) : null
                ) : (
                    <>
                        <LegsByFuelPdfTable estimate={estimate} />
                        {legs.map((leg, index) => {
                            const legAmount = getFormattedAmount(leg.mass.amount)
                            return (
                                <>
                                    <Divider />
                                    <Text variant={'h5'}>
                                        {t`Leg ${index + 1}`}: {legAmount.displayAmount}{' '}
                                        {legAmount.amountUnit}CO₂e
                                    </Text>
                                    <MultiLegShippingByFuelEmissionExplanation
                                        estimate={estimate}
                                        enableEmissionFactorLink={false}
                                        leg={index}
                                        pdfStructure={true}
                                    />
                                </>
                            )
                        })}
                    </>
                )}
                {activeTabIndex !== null && (
                    <>
                        <Divider />
                        <MultiLegShippingByFuelInputSection
                            estimateLeg={estimate.legs[activeTabIndex - 1]}
                            requestLeg={estimate.request.legs[activeTabIndex - 1]}
                        />
                    </>
                )}
                {!pdfStructure && requestAndResponseSnippets && (
                    <>
                        <Divider />
                        <RequestAndResponse
                            path={`/v1/estimates/shipping/multi-leg/by-fuel`}
                            request={(estimate._meta.response as { request: unknown }).request}
                            response={estimate._meta.response}
                            idempotencyKey={
                                (estimate._meta.response as { idempotency_key?: string })
                                    .idempotency_key
                            }
                            metadata={
                                (
                                    estimate._meta.response as {
                                        metadata?: Record<string, string>
                                    }
                                ).metadata
                            }
                            isTestMode={requestAndResponseSnippets.isTestMode}
                            testApiKey={requestAndResponseSnippets.testApiKey}
                        />
                    </>
                )}
            </Stack>
        </>
    )
}

export default MultiLegShippingByFuelEstimateResult
